import { useState, useEffect } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
	getListCorporateDocument,
	getCompanyAdmins,
} from "actions/RFQ/Supplier/rfqActions";
import {
	SmartFieldQuestionnaireBase,
	filterSmartFieldOptionForSubmittedRFQ,
} from "./SmartFieldQuestionnaireBase";
import { useSmartFieldSelection } from "../hooks/useSmartFieldSelection";
import { CorporateDocument } from "pages/RFQ/RFQ/CreateRFQ/PreviewRFQ/Questionnaire/_components/CorporateDocument";
import { filterDocumentsByAllowedCategories } from "pages/RFQSubmission/RFQPreview/RFQUtils/RFQHelper";
import { useDocumentCategories } from "newui/store";
import { isNotNullOrUndefined } from "newui/utils/objectUtil";
import { useRfxSubmissionData } from "newui/features/Rfx/data-provider/RfxSubmissionDataProvider";
import { useCurrentUser, useCurrentCompanyId } from "hooks/auth";
import { FiMail } from "react-icons/fi";
import { AddNewCard } from "./AddNewCard";
import { AddNewDocumentModal } from "./AddNewDocumentModal";
import { ContactAdminDrawer } from "./ContactAdminDrawer";
import { RfxQuestion } from "newui/features/Rfx/types/returnable-schedule-question.type";
import { RfxDocumentCategory } from "newui/features/Rfx/Submission/types/returnable-schedule-answer.type";

interface DocumentItem {
	id: number;
	category?: { name: string };
	file_name?: string;
	size?: number;
	storage_path?: string;
	uuid?: string;
}

interface FilterOption {
	label: string;
	value: number | string;
}

interface PaginationInfo {
	current_page: number;
	has_more_pages: boolean;
	total: number;
}

interface CorporateDocumentPageResponse {
	corporate_document?: DocumentItem[];
	filters?: FilterOption[];
	included_items?: DocumentItem[];
	pagination?: PaginationInfo;
}

interface CompanyAdmin {
	id: number;
	name: string;
	email: string;
}

interface DocumentCategoryOption {
	label: string;
	parent_id?: number | null;
	value: number;
}

interface SmartFieldCorporateDocumentProps {
	name: string;
	[key: string]: unknown;
	answer?: {
		selectedDocuments?: DocumentItem[];
	};
	className?: string;
	disabled?: boolean;
	fieldValue?: {
		allow_document_categories?: RfxDocumentCategory[];
	};
	isClientPreview?: boolean;
	question: RfxQuestion;
	required?: boolean;
	setFieldValue?: (field: string, value: unknown) => void;
}

const documentDummyData: DocumentItem = {
	id: 0,
	file_name: "Example Document Name.pdf",
	category: {
		name: "",
	},
	size: 1000,
};

export const SmartFieldCorporateDocument: React.FC<SmartFieldCorporateDocumentProps> = ({
	className,
	question,
	required,
	isClientPreview,
	answer,
	...props
}) => {
	const [search, setSearch] = useState("");
	const [selectedFilter, setSelectedFilter] = useState<FilterOption[]>([]);
	const [filterParams, setFilterParams] = useState<Record<string, string | number>>({});
	const [showAddModal, setShowAddModal] = useState(false);
	const [showContactDrawer, setShowContactDrawer] = useState(false);
	const { rfxId, rfxData, isClientView } = useRfxSubmissionData();
	const companyId = useCurrentCompanyId();
	const { isCompanyAdmin, currentUser } = useCurrentUser();
	const { value, error, touched, isSelected, selectHandler } =
		useSmartFieldSelection(props);
	const { isLoading: categoriesLoading, values: documentCategories } =
		useDocumentCategories() as { isLoading: boolean; values: DocumentCategoryOption[] };

	// extract questionId from props.name
	const questionId = props.name?.split("-")[1];

	// Capture initial selections so selected items always appear
	const [initialIncludeIds] = useState<number[]>(() =>
		Array.isArray(value) ? value : value ? [value] : []
	);

	// Get allow_document_categories from props
	useEffect(() => {
		if (!props.fieldValue?.allow_document_categories) {
			return;
		}

		const questionId = props.name?.split("-")[1];

		const params: Record<string, string | number> = {};
		if (questionId) {
			params[`filters[question_id]`] = questionId;
		}

		props.fieldValue.allow_document_categories.forEach((item, index) => {
			if (item.category_id) {
				params[`filters[category][${index}]`] = item.category_id;
			}
			if (item.subcategory_id) {
				params[`filters[category][${index}]`] = item.subcategory_id;
			}
		});

		setFilterParams(params);
	}, [props.name, props.fieldValue?.allow_document_categories]);

	const queryParams = {
		search,
		...filterParams,
		...(initialIncludeIds.length > 0 && { include_ids: initialIncludeIds }),
	};

	const useAnswerData =
		(props?.disabled && answer?.selectedDocuments) || !rfxId || isClientView;

	// Get documents
	const {
		data,
		isLoading,
		error: queryError,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isRefetching,
		refetch,
	} = useInfiniteQuery<CorporateDocumentPageResponse>({
		queryKey: ["corporateDocument", queryParams],
		queryFn: (context) =>
			getListCorporateDocument({
				queryKey: context.queryKey,
				pageParam: (context.pageParam as number) ?? 1,
			}),
		initialPageParam: 1,
		getNextPageParam: (lastPage) =>
			lastPage?.pagination?.has_more_pages
				? lastPage.pagination.current_page + 1
				: undefined,
		gcTime: 10 * 60 * 1000,
		enabled:
			Object.keys(filterParams).length > 0 &&
			!useAnswerData &&
			!isClientPreview,
	});

	// Fetch company admins for non-admin info card
	const { data: admins } = useQuery<CompanyAdmin[]>({
		queryKey: ["companyAdmins", companyId],
		queryFn: getCompanyAdmins,
		enabled: !isCompanyAdmin && !props?.disabled && !isClientPreview && !!companyId,
	});

	// Flatten paginated results
	const allPages = data?.pages ?? [];
	const paginatedDocs = allPages.flatMap(
		(page) => page?.corporate_document ?? []
	);
	const includedItems = allPages[0]?.included_items ?? [];
	const filteringList = allPages[0]?.filters ?? [];

	// Merge paginated + included items (deduplicated)
	const mergedDocs = [...paginatedDocs];
	includedItems.forEach((item) => {
		if (!mergedDocs.some((d) => d.id === item.id)) {
			mergedDocs.push(item);
		}
	});

	const documents =
		mergedDocs.length > 0 ? mergedDocs : (answer?.selectedDocuments ?? []);

	// filter by allowed category ids
	const allowedCategoryIds = (
		props.fieldValue?.allow_document_categories || []
	).map((item) => Number(item.category_id));

	const categoryFilteredDocuments = filterDocumentsByAllowedCategories(
		documents,
		allowedCategoryIds
	);

	const filteredData = filterSmartFieldOptionForSubmittedRFQ<DocumentItem>(
		categoryFilteredDocuments,
		value,
		props?.disabled
	);

	const pagination = allPages[allPages.length - 1]?.pagination;
	const remaining = pagination ? pagination.total - paginatedDocs.length : 0;

	if (isClientPreview) {
		const firstAllowedId = allowedCategoryIds?.[0];
		const categoryName = isNotNullOrUndefined(firstAllowedId)
			? documentCategories.find((dc) => dc.value === firstAllowedId)?.label
			: null;
		filteredData.push({
			...documentDummyData,
			category: { name: categoryName ?? "Chosen Category shown here" },
		});
	}

	const handleDocumentCreated = (newId: number): void => {
		selectHandler(newId);
	};

	const showAddNew = !isClientPreview && !isClientView;
	const hasItems = mergedDocs.length > 0;
	const hasVisibleItems = filteredData.length > 0;

	// Build document category name for display
	const firstAllowedCategoryId = allowedCategoryIds?.[0];
	const documentCategoryName = isNotNullOrUndefined(firstAllowedCategoryId)
		? documentCategories.find((dc) => dc.value === firstAllowedCategoryId)?.label ?? "Corporate Document"
		: "Corporate Document";

	// Build subcategory name for display
	const firstAllowedSubcategoryId = (props.fieldValue?.allow_document_categories || [])[0]?.subcategory_id;
	const documentSubcategoryName = isNotNullOrUndefined(firstAllowedSubcategoryId)
		? documentCategories.find((dc) => dc.value === Number(firstAllowedSubcategoryId))?.label
		: null;

	// Get current user's name for the notification
	const requesterName = currentUser
		? `${currentUser.first_name ?? ""} ${currentUser.last_name ?? ""}`.trim()
		: "";

	const handleRefresh = (): void => {
		refetch();
	};

	return (
		<SmartFieldQuestionnaireBase
			{...props}
			className={className}
			question={question}
			required={required}
			description="Please choose the corporate documents that you would like to share."
			searchPlaceholder="Search documents"
			showFilter={false}
			showSearch={!props?.disabled}
			isLoading={isLoading || categoriesLoading}
			loadingMessage="Loading your documents..."
			filteringList={filteringList}
			selectedFilter={selectedFilter}
			setSelectedFilter={setSelectedFilter}
			setSearch={setSearch}
			error={error}
			touched={touched}
			isClientPreview={isClientPreview}
			onAddNew={showAddNew && hasVisibleItems && isCompanyAdmin ? () => setShowAddModal(true) : undefined}
			addNewLabel={showAddNew && hasVisibleItems && isCompanyAdmin ? "Add Document" : undefined}
			onRefresh={showAddNew ? handleRefresh : undefined}
			isRefreshing={isRefetching}
			emptyStateContent={showAddNew && !hasVisibleItems && !isLoading ? (
				<div className="mt-4 w-full">
					<div className="mb-2 text-sm text-gray-500">
						<p className="mb-1 font-medium">
							Category: {documentCategoryName}
							{documentSubcategoryName ? ` / ${documentSubcategoryName}` : ""}
						</p>
						<p className="italic">
							Please add a new document to your profile, to submit it with your response. Once added here, it will be available for any future submissions.
						</p>
					</div>
					{isCompanyAdmin ? (
						<AddNewCard
							label="+ Add Document"
							onClick={() => setShowAddModal(true)}
							disabled={props.disabled}
						/>
					) : (
						<button
							type="button"
							onClick={() => setShowContactDrawer(true)}
							className="flex w-full cursor-pointer items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700 transition-colors hover:border-blue-400 hover:bg-blue-100"
							disabled={!admins || admins.length === 0}
						>
							<FiMail size={16} className="flex-shrink-0" />
							<span className="font-medium underline">
								Contact Company Admin to upload new documents
							</span>
						</button>
					)}
				</div>
			) : undefined}
		>
			{queryError ? (
				<div className="text-red-500">
					Error loading documents: {queryError.message || "Unknown error"}
				</div>
			) : filteredData?.length > 0 ? (
				<div className="w-full">
					<CorporateDocument
						key={`corporate-doc-${questionId}`}
						values={filteredData.map((item) => ({
							...item,
							key: item.id || item.uuid,
						}))}
						selectHandler={selectHandler}
						selected={isSelected}
						isLoading={isLoading || categoriesLoading}
						companyId={companyId}
						isPreview={props?.disabled}
						linkStoragePath
						questionId={questionId}
						setFieldValue={props.setFieldValue}
					/>
					{hasNextPage && !props?.disabled && (
						<div className="mt-4 flex justify-center">
							<button
								type="button"
								onClick={() => fetchNextPage()}
								disabled={isFetchingNextPage}
								className="flex items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-600 transition-colors hover:border-blue-400 hover:text-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
							>
								{isFetchingNextPage ? (
									<div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
								) : null}
								{isFetchingNextPage
									? "Loading..."
									: `Load More Documents (${remaining})`}
							</button>
						</div>
					)}
				</div>
			) : !showAddNew ? (
				<div className="italic text-gray-500">
					No documents {useAnswerData ? "selected" : "found"}
				</div>
			) : null}
			{showAddModal && (
				<AddNewDocumentModal
					isOpen={showAddModal}
					onClose={() => setShowAddModal(false)}
					onCreated={handleDocumentCreated}
					categories={documentCategories}
					allowedCategoryIds={allowedCategoryIds}
					allowedCategories={props.fieldValue?.allow_document_categories ?? []}
				/>
			)}
			{showContactDrawer && admins && admins.length > 0 && companyId && (
				<ContactAdminDrawer
					isOpen={showContactDrawer}
					onClose={() => setShowContactDrawer(false)}
					admins={admins}
					companyId={companyId}
					rfxId={rfxId ?? 0}
					rfxName={rfxData?.name ?? ""}
					rfxType={rfxData?.rfxType?.name ?? rfxData?.type ?? "RFx"}
					projectName={rfxData?.project?.name ?? ""}
					documentCategory={documentCategoryName}
					requesterName={requesterName}
				/>
			)}
		</SmartFieldQuestionnaireBase>
	);
};
